import React, { useContext, useEffect, useMemo, useState } from 'react';
import { differenceInMilliseconds } from 'date-fns';
import Cookies from 'js-cookie';

import SocketContext, { useSocket } from 'utils/hooks/useSocket';
import { useTournament } from 'context/TournamentContext';
import { pairLabelOf } from 'utils/cash';

import { Belt, TName, Countdown, MyRank } from './styled';

const URGENCY_THRESHOLD_MS = 60 * 60 * 1000;

const fmtCountdown = (totalDays: number, hours: number, minutes: number, seconds: number) => {
  if (totalDays > 0) return `${totalDays}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const TournamentBelt: React.FC = () => {
  const [countdownText, setCountdownText] = useState('');
  const [urgent, setUrgent] = useState(false);

  const { tournamentDetails } = useTournament();
  const { rank, tournamentRanking } = useSocket() as any;
  const { selectedCoin } = useContext(SocketContext) as any;

  const rawData: any = tournamentDetails?.data;
  const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
  const tournamentType = attributes?.trade_type;
  const tournamentName = attributes?.name;
  const endAt = attributes?.end_at;

  const username = localStorage.getItem('username') ?? Cookies.get('username') ?? '';
  const rankingArray = Array.isArray(tournamentRanking) ? tournamentRanking : [];

  const activeSym = useMemo(() => {
    const fromName =
      typeof selectedCoin?.name === 'string' ? selectedCoin.name.split('/')[0] : undefined;
    return (fromName ?? selectedCoin?.short_name ?? selectedCoin?.base?.data?.attributes?.short_name ?? '') as string;
  }, [selectedCoin]);
  const activeSymUpper = String(activeSym || '').toUpperCase();
  const pairLabel = pairLabelOf(activeSymUpper, selectedCoin?.name);
  const tradeTypeLabel = typeof tournamentType === 'string' ? tournamentType.toLowerCase() : '';

  useEffect(() => {
    const tick = () => {
      if (!endAt) {
        setCountdownText('');
        setUrgent(false);
        return;
      }
      const end = new Date(endAt);
      const diffMs = differenceInMilliseconds(end, new Date());
      if (diffMs <= 0) {
        setCountdownText('0d 0h 0m');
        setUrgent(false);
        return;
      }
      const totalSec = Math.floor(diffMs / 1000);
      const totalDays = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;
      setCountdownText(fmtCountdown(totalDays, hours, minutes, seconds));
      setUrgent(diffMs < URGENCY_THRESHOLD_MS);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endAt]);

  // Use username to align rank with current user when needed (referenced for parity with TradeHero)
  void username;

  return (
    <Belt>
      <TName>
        {tournamentName ? <b>{tournamentName}</b> : <>tournament</>}
        {tradeTypeLabel ? ` · ${tradeTypeLabel}` : ''}
        {pairLabel ? ` · ${pairLabel}` : ''}
      </TName>
      {countdownText && (
        <Countdown $urgent={urgent}>
          <span className="dot" />
          {countdownText}
        </Countdown>
      )}
      {(typeof rank === 'number' || typeof rank === 'string') && (
        <MyRank>
          Rank <b>#{rank}</b>
          {rankingArray.length > 0 ? `/${rankingArray.length}` : ''}
        </MyRank>
      )}
    </Belt>
  );
};

export default TournamentBelt;
